import { useEffect, useState } from 'react';

import Papa from 'papaparse';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from 'react-virtuoso';

import { Spinner } from '@core/components/ui';
import { logger } from '@core/utils';

/**
 * CSV viewer with parsed table, sticky header, and virtualized rows.
 */
export function CsvViewer({ url }) {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;

    async function fetchAndParse() {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(url);
        if (!response.ok) {
          throw Object.assign(new Error(`Failed to fetch file: ${response.status}`), { status: response.status });
        }
        const text = await response.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });

        if (!cancelled) {
          if (result.errors.length > 0) {
            logger.warn('CSV parse warnings:', result.errors);
          }
          setHeaders(result.meta.fields || []);
          setRows(result.data);
        }
      } catch (err) {
        logger.error('CsvViewer fetch error:', err);
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAndParse();
    return () => { cancelled = true; };
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-[var(--color-danger)]">
        <p className="text-sm">
          {error.status ? t('dataroom.viewer.fetchFailed', { status: error.status }) : error.message}
        </p>
      </div>
    );
  }

  if (headers.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-[var(--color-text-secondary)]">
        <p className="text-sm">{t('dataroom.viewer.csvNoData')}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="overflow-x-auto flex-1">
        <TableVirtuoso
          style={{ height: '100%' }}
          data={rows}
          fixedHeaderContent={() => (
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-4 py-2 font-medium text-[var(--color-text-secondary)] whitespace-nowrap border-b border-[var(--color-border)] bg-[var(--color-bg-muted)]"
                >
                  {header}
                </th>
              ))}
            </tr>
          )}
          itemContent={(index, row) => (
            <>
              {headers.map((header) => (
                <td
                  key={header}
                  className="px-4 py-2 text-[var(--color-text-secondary)] border-b border-[var(--color-bg-muted)] max-w-xs truncate"
                  title={row[header] ?? ''}
                >
                  {row[header] ?? ''}
                </td>
              ))}
            </>
          )}
        />
      </div>
      <div className="px-4 py-1.5 text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] border-t border-[var(--color-border)] shrink-0">
        {t('dataroom.viewer.rowCount', { count: rows.length })} · {t('dataroom.viewer.columnCount', { count: headers.length })}
      </div>
    </div>
  );
}

CsvViewer.propTypes = {
  url: PropTypes.string.isRequired,
};
